/**
 * If you are not familiar with React Navigation, refer to the "Fundamentals" guide:
 * https://reactnavigation.org/docs/getting-started
 *
 */
import { NavigationContainer, NavigationContainerRef, Theme } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import { useRef } from 'react';
import * as Analytics from 'expo-firebase-analytics';
import HomeScreen from '../screens/Site/HomeScreen';
import NotFoundScreen from '../screens/NotFoundScreen';
import { RootStackParamList } from './navigationTypes';
import LinkingConfiguration from './LinkingConfiguration';
import ScreenManager from '../screens/ConversionFlow/ScreenManager';
import { AppContextProvider } from '../context/context';
import Subscription from '../screens/Site/Subscription';
import EmailQuestion from '../screens/ConversionFlow/EmailQuestion';
import Checkout from '../screens/ConversionFlow/Checkout';
import Register from '../screens/ConversionFlow/Register';
import ExpertReviewSignUp from '../screens/ConversionFlow/ExpertReviewSignUp';
import CheckoutRedirect from '../screens/ConversionFlow/CheckoutRedirect';
import CreateAccount from '../screens/ConversionFlow/CreateAccount';
import CreateReferralAccount from '../screens/ConversionFlow/Referral Program/CreateReferralAccount';
import VerifyReferralAccount from '../screens/ConversionFlow/Referral Program/VerifyReferralAccount';
import DownloadApp from '../screens/Site/DownloadApp';
import TermsScreen from '../screens/Site/Terms';
import Beta from '../screens/ConversionFlow/Beta';
import PrivacyScreen from '../screens/Site/Privacy';
import WhatIsSnapCalorie from '../screens/Site/WhatIsSnapCalorie';
import HowMuchDoesSnapCalorieCost from '../screens/Site/HowMuchDoesSnapCalorieCost';
import DoesSnapCalorieWork from '../screens/Site/DoesSnapCalorieWork';
import Upgrade from '../screens/ConversionFlow/Upgrade';

const colorScheme: Theme = {
  dark: false,
  colors: {
    primary: 'black',
    background: 'white',
    card: 'black',
    text: 'black',
    border: 'black',
    notification: 'black',
  },
};

const getRouteString = (route: NavigationContainerRef<RootStackParamList>): string => {
  const currentRoute = route.getCurrentRoute();
  let baseName = currentRoute?.name;
  const params = currentRoute?.params;
  // eslint-disable-next-line no-prototype-builtins
  if (params && params.hasOwnProperty('id')) {
    // eslint-disable-next-line prettier/prettier
    baseName = `${baseName}/${(params as any).id}`;
  }
  return baseName ?? '';
};

export default function Navigation() {
  const routeNameRef = useRef<string>();
  const navigationRef = useRef<NavigationContainerRef<RootStackParamList>>(null);

  return (
    <AppContextProvider>
      <NavigationContainer
        ref={navigationRef}
        linking={LinkingConfiguration}
        theme={colorScheme}
        onReady={() => {
          if (navigationRef.current !== null) routeNameRef.current = getRouteString(navigationRef.current);
          // console.log(`[NAV] Setting current route to ${routeNameRef.current}`);
        }}
        onStateChange={() => {
          if (navigationRef.current === null) return;

          const previousRouteName = routeNameRef.current;
          const currentRouteName = getRouteString(navigationRef.current);

          if (previousRouteName !== currentRouteName) {
            Analytics.logEvent('page_view', {
              screen_name: currentRouteName,
              page_title: currentRouteName,
            });
          }

          // Save the current route name for later comparision
          routeNameRef.current = currentRouteName;
        }}
      >
        <RootNavigator />
      </NavigationContainer>
    </AppContextProvider>
  );
}

/**
 * A root stack navigator is often used for displaying modals on top of all other content.
 * https://reactnavigation.org/docs/modal
 */
const RootStack = createNativeStackNavigator<RootStackParamList>();

function RootNavigator() {
  return (
    <RootStack.Navigator initialRouteName="Home">
      <RootStack.Screen name="Home" component={HomeScreen} options={{ headerShown: false }} />
      <RootStack.Screen name="Subscription" component={Subscription} options={{ headerShown: false }} />
      <RootStack.Screen name="Email" component={EmailQuestion} options={{ headerShown: false }} />
      <RootStack.Screen name="Checkout" component={Checkout} options={{ headerShown: false }} />
      <RootStack.Screen name="Upgrade" component={Upgrade} options={{ headerShown: false }} />
      <RootStack.Screen name="CheckoutRedirect" component={CheckoutRedirect} options={{ headerShown: false }} />
      <RootStack.Screen name="CreateAccount" component={CreateAccount} options={{ headerShown: false }} />
      <RootStack.Screen name="Refer" component={CreateReferralAccount} options={{ headerShown: false }} />
      <RootStack.Screen
        name="ReferralVerification"
        component={VerifyReferralAccount}
        options={{ headerShown: false }}
      />
      <RootStack.Screen name="DownloadApp" component={DownloadApp} options={{ headerShown: false }} />
      <RootStack.Screen name="Register" component={Register} options={{ headerShown: false }} />
      <RootStack.Screen name="ExpertReviewSignUp" component={ExpertReviewSignUp} options={{ headerShown: false }} />
      <RootStack.Screen name="Beta" component={Beta} options={{ headerShown: false }} />
      <RootStack.Screen name="Terms" component={TermsScreen} options={{ headerShown: false }} />
      <RootStack.Screen name="Privacy" component={PrivacyScreen} options={{ headerShown: false }} />
      <RootStack.Screen name="WhatIsSnapCalorie" component={WhatIsSnapCalorie} options={{ headerShown: false }} />
      <RootStack.Screen
        name="HowMuchDoesSnapCalorieCost"
        component={HowMuchDoesSnapCalorieCost}
        options={{ headerShown: false }}
      />
      <RootStack.Screen name="DoesSnapCalorieWork" component={DoesSnapCalorieWork} options={{ headerShown: false }} />
      <RootStack.Screen
        name="Survey"
        component={ScreenManager}
        options={{ headerShown: false }}
        initialParams={{ id: 'Welcome' }}
      />
      <RootStack.Screen name="NotFound" component={NotFoundScreen} options={{ headerShown: false, title: 'Oops!' }} />
    </RootStack.Navigator>
  );
}
